<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { isReviewLive } from '@/cms/reviewGate.js'
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_TYPE,
  DEFAULT_OG_IMAGE_ALT,
  withTrailingSlash
} from '@/utils/siteUrl'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import SplitHeaderBlock from '@/components/SplitHeaderBlock.vue'

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsEditorModal = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditorModal.vue'))
  : null

const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

const route = useRoute()

const modules = import.meta.glob('/src/content/news/*.md', { eager: true })
const slug = computed(() => route.params.slug)

const isEditing   = ref(false)
const contentPath = computed(() => `/src/content/news/${slug.value}.md`)

const frontmatter = computed(() => {
  const mod = modules[contentPath.value]
  if (!mod) return null
  return { title: mod.title, date: mod.date, excerpt: mod.excerpt, author: mod.author, publishedAt: mod.publishedAt || null, reviewStatus: mod.reviewStatus || '', image: mod.image || '' }
})

// A title can carry an explicit line break (e.g. mind-gap.md, per direct
// request) — split rather than relying on white-space:pre-line, so the gap
// between lines is a controlled margin instead of a full extra line-height.
const titleLines = computed(() => (frontmatter.value?.title || '').split('\n'))

const isPublished = computed(() => {
  if (!frontmatter.value) return true
  if (import.meta.env.DEV) return true
  const p = frontmatter.value.publishedAt
  return (!p || new Date(p) <= new Date()) && isReviewLive(frontmatter.value.reviewStatus)
})

const breadcrumbs = computed(() => [
  { label: 'Home', to: '/' },
  { label: 'News', to: '/about/news' },
  { label: frontmatter.value?.title || 'Article' },
])

const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL

// Sitewide default share image (jk-35) unless this article sets its own image.
useHead(computed(() => {
  const fm = frontmatter.value
  const title = fm ? fm.title : 'News'
  const description = fm?.excerpt || ''
  const url = `${baseUrl}${withTrailingSlash(`/about/news/${slug.value}`)}`
  const hasCustomImage = Boolean(fm?.image)
  const ogImage = fm?.image || DEFAULT_OG_IMAGE
  const ogImageAbs = `${baseUrl}${ogImage}`
  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: url },
      { property: 'og:image', content: ogImageAbs },
      { name: 'twitter:image', content: ogImageAbs },
      // Known dimensions only apply to the default image — an article's own
      // image may be a different size, so we don't claim to know it.
      ...(hasCustomImage ? [] : [
        { property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH },
        { property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT },
        { property: 'og:image:type', content: DEFAULT_OG_IMAGE_TYPE },
        { property: 'og:image:alt', content: DEFAULT_OG_IMAGE_ALT }
      ]),
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    link: [{ rel: 'canonical', href: url }],
  }
}))

const lazyModules = import.meta.glob('/src/content/news/*.md')
const MarkdownContent = computed(() => {
  const key = `/src/content/news/${slug.value}.md`
  if (!lazyModules[key]) return null
  return defineAsyncComponent(lazyModules[key])
})

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric'
  })
}
</script>

<template>
  <div class="news-detail container">
    <template v-if="!frontmatter">
      <h1>Article not found</h1>
      <RouterLink to="/about/news/">← Back to news</RouterLink>
    </template>

    <template v-else-if="!isPublished">
      <p class="not-published">This article is not yet available.</p>
      <div class="back">
        <RouterLink to="/about/news/">← Back to news</RouterLink>
      </div>
    </template>

    <template v-else>
      <Breadcrumbs :items="breadcrumbs" />
      <CmsEditorModal v-if="isCmsEnabled && isEditing" :content-path="contentPath" @close="isEditing = false" />

      <SplitHeaderBlock class="layout">
        <template #header>
          <div v-if="showCmsChrome" class="edit-row">
            <button class="edit-inline" @click="isEditing = true">Edit</button>
          </div>

          <h1 class="title">
            <span v-for="(line, i) in titleLines" :key="i" class="line">{{ line }}</span>
          </h1>

          <div class="meta">
            <time :datetime="frontmatter.date">{{ formatDate(frontmatter.date) }}</time>
            <span v-if="frontmatter.author" class="author">{{ frontmatter.author }}</span>
          </div>
        </template>

        <component :is="MarkdownContent" v-if="MarkdownContent" class="body" />

        <div class="back">
          <RouterLink to="/about/news/">← Back to news</RouterLink>
        </div>
      </SplitHeaderBlock>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.news-detail {
  // No max-width override — .container (1440px) applies directly now.
  // Was $content-prose (760px), a narrow single-column article layout;
  // replaced with the shared SplitHeaderBlock grid (see .layout below),
  // matching TrainingDetail's jk-31 follow-up.
  padding-block: $space-8;

  .breadcrumbs { margin-bottom: $space-5; }

  // No margin here — SplitHeaderBlock's own .header already applies a
  // flex gap between its children (edit-row/title/meta).
  .edit-row {
    display: flex;
    justify-content: flex-end;
  }

  .edit-inline {
    background: none;
    border: 1px solid var(--color-border);
    border-radius: $radius-sm;
    color: var(--color-text-muted);
    cursor: pointer;
    font-family: $font-sans;
    font-size: $font-size-cap;
    font-weight: 600;
    padding: 2px $space-2;
    @include transition(color, border-color, background);

    &:hover         { color: var(--color-primary); border-color: var(--color-primary); }
    &:focus-visible { @include focus-ring; }
    &:active        { transform: translateY(1px); }
  }

  .title {
    margin: 0;
    font-size: $font-size-h1;
    font-weight: 700;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;

    // A title can carry an explicit line break (e.g. mind-gap.md, per direct
    // request), split into one .line per line in the template. Each line is
    // its own block so the gap between them is this controlled margin —
    // half a line-height — rather than a full blank line's worth of height.
    .line {
      display: block;
    }

    .line + .line { margin-top: $lh-heading * 0.5em; }
  }

  .meta {
    display: flex;
    gap: $space-3;
    align-items: center;
    font-size: $font-size-sm;
    color: var(--color-text-muted);
  }

  .author::before { content: '·'; margin-right: $space-3; }

  .body {
    line-height: $lh-base;
    color: var(--color-text);

    // The markdown body's own first heading had full default browser
    // margin, throwing the content column out of line with the header
    // column's flush <h1> — zero it (same fix as TrainingDetail).
    > :first-child { margin: 0; }

    // Markdown blockquotes render as the site's quote block — flush to the
    // body's left edge (no browser default indent), a left accent bar, and
    // larger quote text (matches QuoteBlock.vue). The trailing "— Name, role"
    // paragraph is styled as a small muted attribution. `:deep()` is required
    // because the blockquote is rendered by the compiled markdown child
    // component, so it doesn't carry this component's scoped-style attribute.
    :deep(blockquote) {
      margin: $space-6 0;
      padding-left: $space-5;
      border-left: 3px solid var(--color-quote-accent);

      p {
        margin: 0;
        font-size: $font-size-h4;   // one step down from h3, sitewide quote-size review
        line-height: $lh-heading;
        letter-spacing: $ls-heading;
        font-weight: 500;
        color: var(--color-ink);
      }

      p + p {
        margin-top: $space-4;
        font-size: $font-size-sm;
        font-weight: 400;
        letter-spacing: normal;
        line-height: $lh-base;
        color: var(--color-text-muted);
      }
    }

    // External links (opened in a new tab via vite.config.mjs's markdown-it
    // setup) get the same underline-draw treatment as ResourceCard's CTA
    // (`.cta` in src/styles/_components.scss) — reused here rather than that
    // shared class, since a prose link has no hoverable card ancestor to
    // trigger the reveal from.
    :deep(a[target="_blank"]) {
      font-weight: 600;
      color: var(--color-primary);
      text-decoration: none;
      background-image: linear-gradient(currentColor, currentColor);
      background-repeat: no-repeat;
      background-position: 0 100%;
      background-size: 0% 1px;
      padding-bottom: 2px;
      @include transition(background-size);

      &:hover, &:focus-visible { background-size: 100% 1px; }
      &:focus-visible { @include focus-ring; }
    }
  }

  .not-published {
    padding: $space-10 0;
    color: var(--color-text-muted);
    font-style: italic;
  }

  .back {
    margin-top: $space-10;
    padding-top: $space-6;
    border-top: 1px solid var(--color-border);
    font-size: $font-size-sm;
    a { color: var(--color-primary); text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
